import { useState } from "react";
import { toast } from "sonner";
import { IoChatbubbleOutline, IoCreateOutline, IoSaveOutline, IoLinkOutline, IoDocumentTextOutline, IoSend } from "react-icons/io5";
import { RED } from "@/components/DojoShell";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import { openCarnetFile, commentShare, editShareEntry } from "@/lib/api";

const ytId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};
const PERM_LABEL = { view: "Lecture seule", comment: "Lecture + commentaires", edit: "Modification autorisée" };

export default function ShareContent({ share, content, onReload }) {
  const canComment = ["comment", "edit"].includes(share.permission);
  const canEdit = share.permission === "edit";
  const [editing, setEditing] = useState(null);     // entry id
  const [draft, setDraft] = useState({ title: "", body: "" });
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const entries = content?.entries || [];

  const startEdit = (e) => { setEditing(e.id); setDraft({ title: e.title || "", body: e.body || "" }); };
  const saveEdit = async (eid) => {
    setBusy(true);
    try { await editShareEntry(share.id, eid, draft); toast.success("Modifié"); setEditing(null); onReload?.(); }
    catch { toast.error("Modification impossible"); } finally { setBusy(false); }
  };
  const sendComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try { await commentShare(share.id, comment.trim()); setComment(""); toast.success("Commentaire ajouté"); onReload?.(); }
    catch { toast.error("Impossible d'ajouter le commentaire"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="share-content">
      <div style={s.head}>
        <div>
          <div style={s.eyebrow}>Partagé par {share.owner_name}</div>
          <h2 style={s.title}>{share.title || (content?.type === "folder" ? content.folder?.label : "Partage")}</h2>
        </div>
        <span style={s.permBadge}>{PERM_LABEL[share.permission] || "Lecture seule"}</span>
      </div>

      {content?.type === "folder" && content.folder && (
        <div style={s.folderTag}>{content.folder.icon || "📁"} Dossier · {entries.length} élément{entries.length > 1 ? "s" : ""}</div>
      )}

      <div style={s.list}>
        {entries.filter(Boolean).map((e) => (
          <div key={e.id} style={s.card} data-testid={`share-entry-${e.id}`}>
            <div style={s.cardHead}>
              {editing === e.id
                ? <input style={s.input} value={draft.title} onChange={(ev) => setDraft({ ...draft, title: ev.target.value })} data-testid={`share-edit-title-${e.id}`} />
                : <span style={s.cardTitle}>{e.title}</span>}
              {canEdit && editing !== e.id && (
                <button style={s.iconBtn} onClick={() => startEdit(e)} data-testid={`share-edit-${e.id}`}><IoCreateOutline size={15} /></button>
              )}
            </div>
            {editing === e.id ? (
              <>
                <textarea style={{ ...s.input, minHeight: 110, marginTop: 8 }} value={draft.body} onChange={(ev) => setDraft({ ...draft, body: ev.target.value })} data-testid={`share-edit-body-${e.id}`} />
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button style={s.cancelBtn} onClick={() => setEditing(null)}>Annuler</button>
                  <button style={{ ...s.saveBtn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => saveEdit(e.id)} data-testid={`share-edit-save-${e.id}`}><IoSaveOutline size={15} /> Enregistrer</button>
                </div>
              </>
            ) : (
              <>
                {e.body && <div style={s.body}>{e.body}</div>}
                {e.video_url && ytId(e.video_url) && (
                  <div style={{ marginTop: 10 }} data-testid={`share-video-${e.id}`}><YouTubeEmbed url={e.video_url} title={e.title} /></div>
                )}
                {e.file_id && <button style={s.chip} onClick={() => openCarnetFile(e.file_id)} data-testid={`share-file-${e.id}`}><IoDocumentTextOutline size={15} /> {e.filename || "Fichier"}</button>}
                {e.kind === "link" && e.url && <a style={s.chip} href={/^https?:\/\//.test(e.url) ? e.url : `https://${e.url}`} target="_blank" rel="noreferrer"><IoLinkOutline size={15} /> {e.url}</a>}
              </>
            )}
          </div>
        ))}
        {entries.filter(Boolean).length === 0 && <div style={s.empty}>Ce partage est vide pour le moment.</div>}
      </div>

      {canComment && (
        <div style={s.comments} data-testid="share-comments">
          <div style={s.commentsTitle}><IoChatbubbleOutline size={16} /> Commentaires</div>
          {(share.comments || []).map((c) => (
            <div key={c.id} style={s.comment}>
              <div style={s.commentAuthor}>{c.author_name}</div>
              <div style={s.commentText}>{c.text}</div>
            </div>
          ))}
          <div style={s.commentRow}>
            <input style={s.input} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Écris un commentaire…" data-testid="share-comment-input" onKeyDown={(e) => { if (e.key === "Enter") sendComment(); }} />
            <button style={s.sendBtn} disabled={busy} onClick={sendComment} data-testid="share-comment-send"><IoSend size={16} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  head: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" },
  eyebrow: { fontSize: 12, fontWeight: 800, letterSpacing: 1, color: RED, textTransform: "uppercase" },
  title: { fontSize: 26, fontWeight: 900, margin: "4px 0 0", fontFamily: "var(--font-display)", color: "var(--text-primary)" },
  permBadge: { fontSize: 12, fontWeight: 800, padding: "5px 12px", borderRadius: 999, background: "var(--bg-tertiary)", color: "var(--text-secondary)", whiteSpace: "nowrap" },
  folderTag: { display: "inline-block", fontSize: 13, fontWeight: 700, color: "var(--text-secondary)", marginBottom: 12 },
  list: { display: "flex", flexDirection: "column", gap: 14 },
  card: { padding: "16px 18px", borderRadius: 16, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)" },
  cardHead: { display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between" },
  cardTitle: { fontWeight: 800, fontSize: 16, color: "var(--text-primary)" },
  iconBtn: { width: 30, height: 30, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-primary)", cursor: "pointer" },
  body: { color: "var(--text-secondary)", fontSize: 14.5, lineHeight: 1.6, marginTop: 8, whiteSpace: "pre-wrap" },
  chip: { display: "inline-flex", alignItems: "center", gap: 7, marginTop: 10, padding: "9px 12px", borderRadius: 10, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-primary)", fontWeight: 700, fontSize: 13, cursor: "pointer", textDecoration: "none" },
  videoWrap: { position: "relative", width: "100%", paddingTop: "56.25%", borderRadius: 12, overflow: "hidden", marginTop: 10, background: "#000" },
  videoFrame: { position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" },
  empty: { padding: "30px", textAlign: "center", color: "var(--text-secondary)", border: "1px dashed var(--border-c)", borderRadius: 14 },
  input: { flex: 1, padding: "10px 13px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 14, color: "var(--text-primary)", background: "var(--bg-tertiary)", outline: "none", width: "100%", boxSizing: "border-box", fontFamily: "inherit" },
  cancelBtn: { padding: "9px 16px", borderRadius: 10, border: "1px solid var(--border-c)", background: "transparent", color: "var(--text-secondary)", fontWeight: 700, cursor: "pointer" },
  saveBtn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 18px", borderRadius: 10, border: "none", background: RED, color: "#fff", fontWeight: 800, cursor: "pointer" },
  comments: { marginTop: 22, padding: 16, borderRadius: 16, border: "1px solid var(--border-c)", background: "var(--bg-secondary)" },
  commentsTitle: { display: "flex", alignItems: "center", gap: 7, fontWeight: 800, fontSize: 14, color: "var(--text-primary)", marginBottom: 12 },
  comment: { padding: "9px 12px", borderRadius: 10, background: "var(--bg-tertiary)", marginBottom: 8 },
  commentAuthor: { fontSize: 12, fontWeight: 800, color: RED, marginBottom: 2 },
  commentText: { fontSize: 13.5, color: "var(--text-primary)", whiteSpace: "pre-wrap" },
  commentRow: { display: "flex", gap: 8, alignItems: "center", marginTop: 6 },
  sendBtn: { width: 42, height: 42, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 10, border: "none", background: RED, color: "#fff", cursor: "pointer" },
};
